import Button from 'components/_UI/Button'
import Fieldset from 'components/_UI/Fieldset'
import Loader from 'components/_UI/Loader'
import Modal from 'components/_UI/Modal'
import Text from 'components/_UI/Text'
import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import { useForm } from 'react-hook-form'
import styled, { css } from 'styled-components'
import { FullyCentered, HorizontalCenter } from 'styled/alignment/Center'
import { Column } from 'styled/alignment/Column'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent'
import teamsApi from 'api/requests/teams'
import { TEAM_ICON_OPTIONS, getTeamIcon } from '../teamIcons'
import TeamPermissionsPicker, { withImplied } from '../TeamPermissionsPicker'
import useTeamPermissionCatalog from '../useTeamPermissionCatalog'

const iconSelectOptions = TEAM_ICON_OPTIONS.map(option => ({ value: option.value, label: option.label }));

function formatIconOptionLabel({ value, label }) {
    const IconComponent = getTeamIcon(value);

    return (
        <IconOptionRow>
            <IconSwatch>
                <IconComponent size={15} color={panelAccent(colors.secondaryPurple)} />
            </IconSwatch>
            <span>{label}</span>
        </IconOptionRow>
    );
}

function IconGridOption({ data, innerRef, innerProps, isFocused, isSelected }) {
    const IconComponent = getTeamIcon(data.value);

    return (
        <IconGridCell ref={innerRef} {...innerProps} title={data.label} $active={isFocused || isSelected}>
            <IconComponent size={18} color={panelAccent(colors.secondaryPurple)} />
        </IconGridCell>
    );
}

function IconGridMenuList({ children, innerRef, innerProps }) {
    return (
        <IconGridWrap ref={innerRef} {...innerProps}>
            {children}
        </IconGridWrap>
    );
}

const iconSelectComponents = { MenuList: IconGridMenuList, Option: IconGridOption };

export default function TeamFormModal({ visible, edit, onClose, onSuccess }) {
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm();

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [icon, setIcon] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const { implications, loading: catalogLoading } = useTeamPermissionCatalog();

    useEffect(() => {
        if (edit) {
            setValue('name', edit?.name);
            setIcon(edit?.icon || null);
        }
    }, [edit, setValue]);

    // Equipes antigas guardam só o que foi marcado na época; a tela mostra
    // também o que cada permissão já inclui, que é o que vale de verdade.
    useEffect(() => {
        if (edit && !catalogLoading) {
            setPermissions(withImplied(edit?.permissions || [], implications));
        }
    }, [edit, catalogLoading]);

    const hasAnyPermission = permissions.length > 0;
    const PreviewIcon = getTeamIcon(icon);
    const selectedIconOption = iconSelectOptions.find(option => option.value === icon) || null;

    async function onSubmit({ name }) {
        const payload = { name, icon, permissions };

        setErrorMessage(false);
        setLoading(true);

        try {
            if (edit) {
                await teamsApi.update(edit.id, payload);
            } else {
                await teamsApi.create(payload);
            }
            onSuccess();
        } catch ({ response }) {
            setErrorMessage(response?.data?.message)
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal isOpen={visible} closeButton={true} onClose={onClose} size='medium'>
            <Container onSubmit={handleSubmit(onSubmit)}>
                <ModalHeader>
                    <IconCircle>
                        <PreviewIcon size={20} color={panelAccent(colors.secondaryPurple)} />
                    </IconCircle>
                    <HeaderTitle>{!edit ? 'Nova equipe' : 'Editar equipe'}</HeaderTitle>
                </ModalHeader>

                <Column marginTop={'20px'}>
                    <Fieldset
                        marginTop='10px'
                        label='Nome da equipe'
                        register={register}
                        name='name'
                        validations={{
                            required: 'Informe um nome',
                            maxLength: { value: 255, message: 'Máximo de 255 caracteres' },
                        }}
                    />

                    {
                        !errorMessage && errors.name && (
                            <HorizontalCenter>
                                <Text color={colors.danger}>
                                    {errors.name?.message}
                                </Text>
                            </HorizontalCenter>
                        )
                    }

                    {
                        errorMessage && !errors.name && (
                            <HorizontalCenter>
                                <Text color={colors.danger}>
                                    {errorMessage}
                                </Text>
                            </HorizontalCenter>
                        )
                    }
                </Column>

                <Column marginTop={'20px'}>
                    <SideTitle>Ícone da equipe (opcional)</SideTitle>
                    <Select
                        options={iconSelectOptions}
                        value={selectedIconOption}
                        onChange={(selected) => setIcon(selected?.value || null)}
                        formatOptionLabel={formatIconOptionLabel}
                        components={iconSelectComponents}
                        placeholder='Selecione um ícone'
                        noOptionsMessage={() => 'Nenhum ícone encontrado'}
                        isClearable
                    />
                </Column>

                <Column marginTop={'20px'}>
                    <SideTitle>Permissões da equipe</SideTitle>
                    <Text color={colors.nightLight} marginTop={'-2px'}>
                        Cada permissão libera uma parte do painel do produtor. Todo membro que entrar nesta equipe recebe exatamente essas permissões — não dá pra configurar diferente por pessoa. Marque pelo menos uma.
                    </Text>

                    <SwitchContainer marginTop={'4px'}>
                        <TeamPermissionsPicker value={permissions} onChange={setPermissions} />
                    </SwitchContainer>
                </Column>

                {
                    loading ? (
                        <FullyCentered height='60px'>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        <ButtonsContainer>
                            <Button
                                variant={'light'}
                                category='roundedBorded'
                                onClick={onClose}
                            >
                                Cancelar
                            </Button>

                            <Button
                                variant={!hasAnyPermission ? 'disabled' : 'lightBlue'}
                                category='roundedBorded'
                                type={'submit'}
                            >
                                {!edit ? 'Criar equipe' : 'Salvar'}
                            </Button>
                        </ButtonsContainer>
                    )
                }
            </Container>
        </Modal>
    )
}

const Container = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: left;
    justify-content: flex-start;
    align-items: flex-start;
    height: 100%;
`;

const ButtonsContainer = styled(Column)`
    margin-top: 20px;

    @media screen and (min-width: ${breakpoints.md}) {
        margin-top: 40px;
        flex-direction: row;

        button:first-child {
            margin-right: 16px;
        }
    }
`;

const SwitchContainer = styled(Column)`
    & > div > label {
        font-size: ${fonts.sizes.default} !important;
    }
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const IconCircle = styled.div`
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    flex-shrink: 0;
`;

const HeaderTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const SideTitle = styled.h4`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
    margin: 0 0 6px;
`;

const IconOptionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const IconSwatch = styled.span`
    width: 26px;
    height: 26px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    flex-shrink: 0;
`;

const IconGridWrap = styled.div`
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 8px;
    padding: 12px;
    max-height: 240px;
    overflow-y: auto;
`;

const IconGridCell = styled.div`
    width: 100%;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    cursor: pointer;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    transition: background 0.12s ease, transform 0.1s ease;

    &:active {
        transform: scale(0.94);
    }

    ${({ $active }) => $active && css`
        background: ${panelAccentAlpha(colors.occasionalPurple, '30')};
    `}
`;
